import { useEffect, useState, useRef, useCallback, type ReactNode } from 'react';
import { haptic } from '../../utils/haptics';

type ToastVariant = 'info' | 'success' | 'error';

const variantIconColor: Record<ToastVariant, string> = {
  info:    'text-accent',
  success: 'text-sage',
  error:   'text-red-400',
};

interface ToastProps {
  message: string;
  subtext?: string;
  icon?: ReactNode;
  variant?: ToastVariant;
  onDismiss: () => void;
  autoDismissMs?: number;
}

export function Toast({
  message,
  subtext,
  icon,
  variant = 'info',
  onDismiss,
  autoDismissMs = 4000,
}: ToastProps) {
  const [leaving, setLeaving] = useState(false);
  const iconColor = variantIconColor[variant];

  // ── Swipe-to-dismiss state ──
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const [dragY, setDragY] = useState(0);
  const isDragging = useRef(false);
  const didSwipe = useRef(false);

  useEffect(() => { haptic(); }, []);

  useEffect(() => {
    const dismissTimer = setTimeout(() => {
      setLeaving(true);
    }, autoDismissMs);
    return () => clearTimeout(dismissTimer);
  }, [autoDismissMs]);

  useEffect(() => {
    if (!leaving) return;
    const exitTimer = setTimeout(() => {
      onDismiss();
    }, 250);
    return () => clearTimeout(exitTimer);
  }, [leaving, onDismiss]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    isDragging.current = false;
    didSwipe.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const dy = e.touches[0].clientY - touchStartY.current;
    // Only track upward drags
    if (dy < -5) {
      isDragging.current = true;
      setDragY(dy);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) {
      setDragY(0);
      return;
    }

    const elapsed = Date.now() - touchStartTime.current;
    const velocity = Math.abs(dragY) / Math.max(elapsed, 1);

    if (Math.abs(dragY) > 30 || velocity > 0.3) {
      didSwipe.current = true;
      setLeaving(true);
    } else {
      setDragY(0);
    }
    isDragging.current = false;
  }, [dragY]);

  const handleClick = useCallback(() => {
    // Don't fire click if user just swiped
    if (didSwipe.current) return;
    setLeaving(true);
  }, []);

  const transform = isDragging.current && dragY < 0
    ? `translateY(${dragY}px)`
    : undefined;

  return (
    <div
      role="alert"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={transform ? { transform } : undefined}
      className={`mx-3 mt-2 backdrop-blur-sm bg-slate-800/90 border border-slate-700/50 rounded-xl shadow-lg px-4 py-2.5 flex flex-col items-center cursor-pointer ${
        leaving ? 'animate-toast-out' : 'animate-toast-in'
      }`}
    >
      {/* Swipe hint bar */}
      <div className="w-8 h-0.5 rounded-full bg-slate-600/60 mb-2 flex-shrink-0" />
      <div className="flex items-start gap-3 w-full">
        {icon && <div className={`flex-shrink-0 mt-0.5 ${iconColor}`}>{icon}</div>}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-100">{message}</p>
          {subtext && (
            <p className="text-xs text-slate-400 mt-0.5">{subtext}</p>
          )}
        </div>
      </div>
    </div>
  );
}
